import { useEffect, useRef, useState } from "react";
import { useQuery } from "convex/react";
import {
  securityRoomStore,
  setSecurityRoomStreamMode,
  selectOpenApprovalsCount,
  selectBlocks24hCount,
  selectDlpHits24hCount,
  appendSecurityRoomEvent,
} from "../stores/securityRoomStore";
import { subscribeAdminActivity } from "../api/client";
import type { ActivityStreamHandle } from "../api/client";
import { api as convexApi } from "../lib/convexApi";
import { Sidebar } from "./securityRoom/Sidebar";
import { normalizeAuditRow, normalizeDlpRows, isoFrom } from "./securityRoom/auditNormalize";
import { ControlPlaneBanner } from "./securityRoom/shared/ControlPlaneBanner";
import { useSrRouteHash } from "./securityRoom/useSrRouteHash";
import { OverviewPage } from "./securityRoom/pages/OverviewPage";
import { ApprovalsPage } from "./securityRoom/pages/ApprovalsPage";
import { AnomaliesPage } from "./securityRoom/pages/AnomaliesPage";
import { HighRiskPage } from "./securityRoom/pages/HighRiskPage";
import { BlocksPage } from "./securityRoom/pages/BlocksPage";
import { DLPPage } from "./securityRoom/pages/DLPPage";
import { PRRiskPage } from "./securityRoom/pages/PRRiskPage";
import { MemoryPage } from "./securityRoom/pages/MemoryPage";
import { AuditPage } from "./securityRoom/pages/AuditPage";
import { PolicyPacksPage } from "./securityRoom/pages/PolicyPacksPage";
import { ExceptionsPage } from "./securityRoom/pages/ExceptionsPage";
import { AiOverviewPage } from "./securityRoom/pages/AiOverviewPage";
import {
  AiSessionsPage,
  DEFAULT_SESSIONS_VIEW,
  type SessionsView,
} from "./securityRoom/pages/AiSessionsPage";
import { AiSessionPage } from "./securityRoom/pages/AiSessionPage";
import { AiSpendPage } from "./securityRoom/pages/AiSpendPage";
import { AiGatewayPage } from "./securityRoom/pages/AiGatewayPage";
import { FallbackBoundary } from "./securityRoom/shared/FallbackBoundary";

// Snapshot is reactive (useQuery) since Spec 5.1 PR 5 — REFRESH_RATE_MS
// (the old rate-limited refresh window) is gone. Convex pushes new
// state on each underlying mutation; the React reconciler debounces
// renders. liveActivityEvents continues to come through the
// subscribeAdminActivity SSE stream as it did before — that's a
// separate channel from the page snapshot.

export function SecurityRoomViewNew() {
  const [route, navigate, routeParam] = useSrRouteHash();
  // The AI sessions list's period and filter, kept here while a session is
  // open (the list unmounts), so Back returns to the same view.
  const [aiSessionsView, setAiSessionsView] = useState<SessionsView>(DEFAULT_SESSIONS_VIEW);
  const snap = securityRoomStore.useStore();

  // Spec 5.1 PR 5 — /api/security-room migrated to convex:
  // api.security_room.queries.snapshot. Reactive useQuery removes the
  // need for periodic refresh() polling; per
  // [[feedback-spec5-pr54-64-review-patterns]] #14 we MERGE the snapshot
  // into the store rather than wholesale-replacing so the
  // liveActivityEvents stream continues to populate live without being
  // clobbered on each Convex recompute.
  const convexSnapshot = useQuery((convexApi as any).security_room.queries.snapshot);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  useEffect(() => {
    if (!mountedRef.current) return;
    if (convexSnapshot === undefined) return;
    // C75-1: map the Convex snapshot to the store keys that
    // SecurityRoomViewNew's pages + selectors actually read. Previously
    // we only wrote ad-hoc `securityRoom*` keys and never hydrated
    // commandAudit / highRiskSessions / dlpHits / summary / exceptions
    // (the existing selectors look at these). After a reload the
    // selectors saw empty arrays even when the Convex snapshot
    // contained data.
    //
    // G75-2..5: every `new Date(x).toISOString()` is guarded against
    // missing/invalid x — `new Date(undefined).toISOString()` throws
    // RangeError and crashes the render.
    const toIso = isoFrom;

    // Project recentEvents into the legacy commandAudit shape that the
    // AuditPage + selectors read. normalizeAuditRow is what FLATTENS the
    // verdict — `details.verdict.decision` → `decision`/`severity` — which the
    // selectors have always read but nothing ever populated. Without it the
    // Blocks page, the "Blocks (24h)" metric, "Critical · Now", the audit
    // "Block" filter and the activity chart all rendered denies as allows.
    // Every list is guarded. A snapshot arriving mid-flight (or a query shape
    // change) with a missing array would otherwise throw inside the effect and
    // blank the whole Security Room — which reads as "nothing happened".
    const asList = (v: unknown): any[] => (Array.isArray(v) ? v : []);
    const commandAudit = asList(convexSnapshot.recentEvents).map((e: any) => normalizeAuditRow(e));
    // A DLP hit can ride on a NON-dlp event (an inline match on a command or
    // terminal row). Filtering by type prefix alone dropped those from the DLP
    // page and the Overview metric while the Audit page — which matches on the
    // ids — still showed them, so the two surfaces disagreed about the same
    // event.
    const dlpHits = commandAudit
      .filter((e: any) => {
        if (typeof e.type === "string" && e.type.startsWith("dlp")) return true;
        if (typeof e.patternId === "string" && e.patternId) return true;
        const ids = e.detail?.patternIds;
        return Array.isArray(ids) && ids.length > 0;
      })
      .flatMap((e: any) => normalizeDlpRows(e));
    const highRiskSessions = asList(convexSnapshot.briefs)
      .filter((b: any) => typeof b.score === "number" && b.score >= 70)
      .map((b: any) => ({
        id: String(b.id),
        prUrl: b.prUrl,
        score: b.score,
        summary: b.summary,
        factors: b.factors ?? [],
        recordedAt: toIso(b.createdAt),
      }));

    securityRoomStore.setState(
      (current) =>
        ({
          ...current,
          error: undefined,
          pendingApprovals: asList(convexSnapshot.pendingApprovals).map((a: any) => ({
            id: a.id,
            status: a.status,
            recordedAt: toIso(a.createdAt),
            createdAt: toIso(a.createdAt),
            agent: a.context?.agent || "",
            repo: a.context?.repo || "",
            command: a.command,
            cmd: a.command,
            rule: a.context?.rule || "",
            requestedBy: a.requestedBy,
          })),
          approvals: asList(convexSnapshot.pendingApprovals).map((a: any) => ({
            id: a.id,
            status: a.status,
            recordedAt: toIso(a.createdAt),
            createdAt: toIso(a.createdAt),
            command: a.command,
            requestedBy: a.requestedBy,
            context: a.context,
          })),
          // C75-1: hydrate the store keys the existing selectors read.
          commandAudit,
          dlpHits,
          highRiskSessions,
          exceptions: convexSnapshot.exceptions,
          securityExceptions: convexSnapshot.exceptions,
          summary: convexSnapshot.stats ?? current.summary,
          // Existing ad-hoc keys preserved so any consumer of those still
          // reads valid data.
          securityRoomNotes: convexSnapshot.notes,
          securityRoomBriefs: convexSnapshot.briefs,
          securityRoomExceptions: convexSnapshot.exceptions,
        }) as Record<string, unknown>
    );
  }, [convexSnapshot]);

  useEffect(() => {
    let handle: ActivityStreamHandle | null = null;
    handle = subscribeAdminActivity(
      (event) => {
        appendSecurityRoomEvent(event);
      },
      (mode) => {
        setSecurityRoomStreamMode(mode);
      }
    );
    setSecurityRoomStreamMode(handle.mode);
    return () => {
      if (handle && typeof handle.close === "function") handle.close();
    };
  }, []);

  // Anomalies come from Convex, not the SSE-fed store, so the badge needs its
  // own subscription. It is the SAME query the page runs — Convex dedupes
  // identical subscriptions, so this costs nothing extra while the page is open
  // and keeps the badge live while it is not.
  const anomalies = useQuery((convexApi as any).anomaly.queries.activeForOrg, {}) as
    | Array<{ status: string }>
    | undefined;

  // Admin-only nav items (the AI gateway) need the role; Convex enforces it
  // too. Read in its own boundary: a failed read shows the nav as a member's.
  const [isAdmin, setIsAdmin] = useState(false);

  const badges = {
    approvals: selectOpenApprovalsCount(snap),
    anomalies: (anomalies ?? []).filter((a) => a?.status === "open").length,
    blocks: selectBlocks24hCount(snap),
    dlp: selectDlpHits24hCount(snap),
  };

  return (
    <div className="sr-shell" data-screen-label="Security Room">
      {/* Keyed by route: after a failed read (shown as a member's nav), the
          next page asks again rather than keeping the failure for good. */}
      <FallbackBoundary
        key={route}
        label="Security Room role"
        fallback={<ReportAdmin isAdmin={false} onAdmin={setIsAdmin} />}
      >
        <AdminProbe onAdmin={setIsAdmin} />
      </FallbackBoundary>
      <Sidebar route={route} badges={badges} onNavigate={navigate} isAdmin={isAdmin} />
      <section className="sr-pane">
        {/* Standing health strip — renders only when a control is degraded, so
            a screen full of zeroes can never be mistaken for "all clear". */}
        <ControlPlaneBanner />
        {route === "overview" && <OverviewPage />}
        {route === "approvals" && <ApprovalsPage />}
        {route === "anomalies" && <AnomaliesPage />}
        {route === "high-risk" && <HighRiskPage />}
        {route === "blocks" && <BlocksPage />}
        {route === "dlp" && <DLPPage />}
        {route === "ai-overview" && (
          <AiOverviewPage onOpenSession={(id) => navigate("ai-sessions", id)} />
        )}
        {route === "ai-sessions" && !routeParam && (
          <AiSessionsPage
            onOpen={(id) => navigate("ai-sessions", id)}
            view={aiSessionsView}
            onViewChange={setAiSessionsView}
          />
        )}
        {route === "ai-sessions" && routeParam && (
          <AiSessionPage sessionId={routeParam} onBack={() => navigate("ai-sessions")} />
        )}
        {route === "ai-spend" && (
          <AiSpendPage onOpenSession={(id) => navigate("ai-sessions", id)} />
        )}
        {route === "pr-risk" && <PRRiskPage />}
        {route === "memory" && <MemoryPage />}
        {route === "audit" && <AuditPage isAdmin={isAdmin} />}
        {route === "policy-packs" && <PolicyPacksPage />}
        {route === "exceptions" && <ExceptionsPage />}
        {route === "ai-gateway" && <AiGatewayPage />}
      </section>
    </div>
  );
}

// Reports whether the viewer is an org admin once the role has loaded. While
// it loads it reports nothing, so the shell keeps what it knew (false at
// first), and reading it again on another page does not hide an admin's nav.
function AdminProbe({ onAdmin }: { onAdmin: (isAdmin: boolean) => void }) {
  const myRole = useQuery((convexApi as any).billing.queries.myRole, {}) as
    | { isAdmin?: boolean }
    | undefined;
  if (myRole === undefined) return null;
  return <ReportAdmin isAdmin={myRole.isAdmin === true} onAdmin={onAdmin} />;
}

function ReportAdmin({
  isAdmin,
  onAdmin,
}: {
  isAdmin: boolean;
  onAdmin: (isAdmin: boolean) => void;
}) {
  useEffect(() => onAdmin(isAdmin), [isAdmin, onAdmin]);
  return null;
}
